import { useState } from 'react';
import { ActivityIcon } from './creativeUi';
import { CreditCostNotice } from '../../credits/CreditCostNotice';

// Card da publicação proposta pelo agente: o usuário revisa a legenda (pode editar), vê as
// contas, o horário e a automação, e aprova ou recusa. Nada vai para as redes sem isso.

const PLATFORM_LABEL: Record<string, string> = {
  instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', youtube: 'YouTube', linkedin: 'LinkedIn', threads: 'Threads', twitter: 'X',
};

const STATUS: Record<string, { label: string; cls: string }> = {
  proposed: { label: 'Aguardando você', cls: 'bg-amber-100 text-amber-800' },
  publishing: { label: 'Enviando ao Zernio…', cls: 'bg-indigo-100 text-indigo-700' },
  scheduled: { label: 'Agendado', cls: 'bg-emerald-100 text-emerald-700' },
  published: { label: 'Publicado', cls: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Falhou', cls: 'bg-rose-100 text-rose-700' },
  rejected: { label: 'Recusado', cls: 'bg-rose-100 text-rose-700' },
  superseded: { label: 'Substituído', cls: 'bg-slate-100 text-slate-500' },
};

function formatWhen(p: any) {
  if (p.publishNow) return 'Agora, assim que aprovar';
  if (!p.scheduledFor) return '—';
  const d = new Date(`${p.scheduledFor}:00-03:00`);
  return Number.isNaN(+d) ? p.scheduledFor : d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
}

export function PublicationCard({ p, canDecide, onDecide }: {
  p: any;
  canDecide: boolean;
  onDecide: (publicationId: string, decision: 'approve' | 'reject', extra?: { caption?: string; tiktokCaption?: string; reason?: string; aiLabel?: boolean }) => Promise<void>;
}) {
  const [caption, setCaption] = useState<string>(p.caption || '');
  // Rótulo de IA nativo da rede: decisão SÓ do usuário, sempre começa desmarcado (o agente não liga).
  const [aiLabel, setAiLabel] = useState(false);
  const aiLabelPlatforms = (p.accounts || []).filter((a: any) => ['instagram', 'tiktok', 'youtube'].includes(a.platform));
  const hasTikTok = (p.accounts || []).some((a: any) => a.platform === 'tiktok');
  const fallbackContent = [caption.trim(), (p.hashtags || []).map((h: string) => `#${h}`).join(' ')].filter(Boolean).join('\n\n');
  const [tiktokCaption, setTikTokCaption] = useState<string>(p.tiktokCaption || '');
  const tiktokContent = tiktokCaption.trim() || fallbackContent;
  const tiktokTooLong = hasTikTok && tiktokContent.length > 90;
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const st = STATUS[p.status] || STATUS.proposed;
  const editable = p.status === 'proposed' && canDecide;
  const decide = async (d: 'approve' | 'reject') => {
    setBusy(d);
    try { await onDecide(p.publicationId, d, d === 'approve' ? { caption, ...(hasTikTok ? { tiktokCaption } : {}), aiLabel } : { reason: reason.trim() || undefined }); } finally { setBusy(null); }
  };

  return (
    <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${p.status === 'proposed' ? 'border-amber-300 ring-2 ring-amber-50' : 'border-slate-200'}`}>
      <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3">
        <ActivityIcon kind="schedule" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-green-600">{p.publishNow ? 'Publicação' : 'Agendamento'}</p>
          <p className="truncate text-base font-bold text-slate-900">{p.deliverable?.title}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${st.cls}`}>{st.label}</span>
      </div>
      <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row">
        <div className="w-full shrink-0 sm:w-32">
          {p.deliverable?.cover && (p.deliverable.coverType === 'video'
            ? <video src={p.deliverable.cover} muted playsInline className="aspect-[4/5] w-full rounded-xl bg-black object-cover" />
            : <img src={p.deliverable.cover} alt="" className="aspect-[4/5] w-full rounded-xl object-cover" />)}
          <p className="mt-1 text-center text-[11px] text-slate-500">{p.deliverable?.kind === 'carousel' ? `Carrossel · ${p.deliverable?.count} slides` : p.deliverable?.kind === 'reel' ? 'Reel' : p.deliverable?.kind === 'story' ? 'Story' : 'Post'}</p>
        </div>
        <div className="min-w-0 flex-1 space-y-3 text-sm">
          {p.instagramTrial && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-indigo-900">
              <p className="font-semibold">Reel de teste · Instagram</p>
              <p className="mt-1 text-xs">Inicialmente exibido a não seguidores. {p.instagramTrial.graduationStrategy === 'SS_PERFORMANCE' ? 'Liberação automática para todos conforme o desempenho.' : 'Liberação para todos somente manualmente pelo app do Instagram.'}</p>
              {(p.accounts || []).some((a: any) => a.platform !== 'instagram') && <p className="mt-1 text-xs">Nas outras redes selecionadas, a publicação será normal.</p>}
            </div>
          )}
          {editable && aiLabelPlatforms.length > 0 && (
            <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">
              <input type="checkbox" checked={aiLabel} onChange={e => setAiLabel(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500" />
              <span>
                <span className="font-semibold">Marcar como conteúdo feito com IA</span>
                <span className="mt-0.5 block text-xs text-slate-500">Ativa o rótulo nativo da rede ({aiLabelPlatforms.map((a: any) => PLATFORM_LABEL[a.platform] || a.platform).join(', ')}). Opcional: deixe desmarcado se não quiser o rótulo.</span>
              </span>
            </label>
          )}
          {!editable && p.aiLabel && (
            <p className="text-xs text-slate-500"><span className="font-semibold">Rótulo de IA da rede:</span> ativado por você.</p>
          )}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Legenda {editable && <span className="normal-case text-slate-400">(pode editar antes de aprovar)</span>}</p>
            {editable
              ? <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={5} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-100" />
              : <p className="mt-1 whitespace-pre-wrap text-slate-700">{p.caption}</p>}
            {Array.isArray(p.hashtags) && p.hashtags.length > 0 && <p className="mt-1 text-xs text-sky-700">{p.hashtags.map((h: string) => `#${h}`).join(' ')}</p>}
          </div>
          {hasTikTok && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Legenda do TikTok</p>
              {editable
                ? <textarea value={tiktokCaption} onChange={e => setTikTokCaption(e.target.value)} placeholder="Versão curta exclusiva do TikTok" rows={2} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-100" />
                : <p className="mt-1 whitespace-pre-wrap text-slate-700">{p.tiktokCaption || fallbackContent}</p>}
              <p className={`mt-1 text-xs ${tiktokTooLong ? 'text-rose-600' : 'text-slate-500'}`}>{tiktokContent.length}/90 caracteres · inclui espaços, emojis e hashtags. As hashtags gerais não são adicionadas à versão exclusiva.</p>
              {editable && tiktokTooLong && <p className="mt-1 text-xs text-rose-600">Escreva uma versão de até 90 caracteres para aprovar.</p>}
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-xl bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Onde</p>
              {(p.accounts || []).map((a: any) => <p key={a.accountId} className="text-slate-700">{PLATFORM_LABEL[a.platform] || a.platform} · @{a.username}</p>)}
            </div>
            <div className="rounded-xl bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Quando (Brasília)</p>
              <p className="text-slate-700 first-letter:uppercase">{formatWhen(p)}</p>
            </div>
          </div>
          {p.firstComment && <p className="text-xs text-slate-500"><span className="font-semibold">Primeiro comentário:</span> {p.firstComment}</p>}
          {p.automation && (
            <div className="rounded-xl border border-green-200 bg-green-50/60 px-3 py-2 text-xs text-green-900">
              <p className="font-bold">Automação de comentário · {PLATFORM_LABEL[p.automation.platform] || p.automation.platform}</p>
              <p>Palavras: {(p.automation.keywords || []).length ? p.automation.keywords.join(', ') : 'qualquer comentário'}</p>
              {p.automation.replyType === 'openai' ? (
                <div className="mt-2 space-y-2 border-t border-green-200 pt-2">
                  <p className="font-semibold">Responder comentários com IA</p>
                  <div>
                    <p className="font-semibold">Prompt proposto para o agente</p>
                    <p className="mt-1 max-h-80 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-green-200 bg-white p-3 text-slate-700">{p.automation.openaiSystemPrompt}</p>
                  </div>
                  <p><span className="font-semibold">Base de conhecimento:</span> {p.automation.useKnowledgeBase === true ? 'Ligada' : 'Desligada'}</p>
                  <p><span className="font-semibold">Injetar legenda no prompt:</span> {p.automation.injectCaption === true ? 'Ligado — usa a legenda final aprovada desta rede' : 'Desligado'}</p>
                  <CreditCostNotice items={[{ featureKey: 'crm_ai_interaction', qty: 1, label: 'resposta de comentário com IA' }]} perUnit note="Cada resposta de comentário com IA consome créditos, pelo mesmo valor do ChatCRM. Sem saldo, a IA não responde." />
                  {editable && <p>Ao aprovar a publicação, você também ativa esta automação com cobrança por resposta. Para alterar o prompt ou as opções propostas, use Recusar / ajustar.</p>}
                </div>
              ) : p.automation.commentReply && <p>Resposta pública: {p.automation.commentReply}</p>}
              {p.automation.dmTrigger
                ? <div className="mt-2 space-y-1 border-t border-green-200 pt-2">
                    <p className="font-semibold">TikTok · resposta à palavra exata na DM</p>
                    <p>Enviar na DM: {p.automation.dmTrigger.keywords.join(', ')}</p>
                    <p>Resposta: {p.automation.dmTrigger.message}</p>
                    <p>O comentário convida a seguir e mandar a palavra na DM. A entrega só acontece após a mensagem recebida, sem checagem de seguidor.</p>
                    <p>O gatilho vale para as DMs desta conta, independentemente de qual post trouxe a pessoa.</p>
                  </div>
                : p.automation.followerCheck?.enabled
                ? <div className="mt-2 space-y-1 border-t border-green-200 pt-2">
                    <p className="font-semibold">Checar se é seguidor · Instagram</p>
                    <p>DM inicial: {p.automation.followerCheck.promptMessage}</p>
                    <p>Botão: {p.automation.followerCheck.buttonText}</p>
                    <p>Quem segue recebe: {p.automation.followerCheck.followerMessage}</p>
                    <p>Quem não segue recebe: {p.automation.followerCheck.nonFollowerMessage}</p>
                  </div>
                : p.automation.dmMessage && <p>Direct: {p.automation.dmMessage}</p>}
              {p.status === 'scheduled' && <p className="mt-1 text-green-700">Entra em funcionamento quando o post for publicado.</p>}
            </div>
          )}
          {p.status === 'failed' && p.error && <p className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">{p.error}</p>}
          {p.automationError && <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">O post foi enviado, mas a automação não foi confirmada: {p.automationError}</p>}
          {editable && (
            <div className="space-y-2">
              {rejecting && (
                <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="O que mudar? (opcional)" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-100" />
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={!!busy || !caption.trim() || tiktokTooLong} onClick={() => decide('approve')} className="rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-50">
                  {busy === 'approve' ? 'Enviando…' : p.publishNow ? 'Aprovar e publicar' : 'Aprovar e agendar'}
                </button>
                {rejecting
                  ? <button type="button" disabled={!!busy} onClick={() => decide('reject')} className="rounded-xl border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">{busy === 'reject' ? 'Enviando…' : 'Confirmar recusa'}</button>
                  : <button type="button" disabled={!!busy} onClick={() => setRejecting(true)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Recusar / ajustar</button>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
